/**
 * PolicySimulatorPage — `/admin/policies/:policyId/simulate`
 *
 * Lets an operator paste a synthetic evaluation context and observe the
 * outcome + per-condition trace. Powered by the in-browser mock evaluator
 * (`simulatePolicy()` in `policies-api.ts`) which will be swapped for a
 * server call once ADR-103 backend ships.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  CaretRight,
  CheckCircle,
  Flask,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Button } from '../../shared/ui/Button';
import { Card, CardBody, CardHeader } from '../../shared/ui/Card';
import { FieldError } from '../../shared/ui/FieldError';
import { Label } from '../../shared/ui/Label';
import { StatusBadge } from '../../shared/ui/StatusBadge';
import { cn } from '../../shared/utils/cn';
import { getSampleContext } from '../../features/business-policies/simulator-samples';
import {
  useBusinessPolicy,
  useSimulatePolicy,
} from '../../features/business-policies/useBusinessPolicies';
import {
  simulatorInputSchema,
  type SimulatorInputValues,
  type SimulatorResult,
} from '../../features/business-policies/types';

export function PolicySimulatorPage(): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const params = useParams<{ policyId: string }>();
  const decodedId = params.policyId ? decodeURIComponent(params.policyId) : undefined;
  const policyQuery = useBusinessPolicy(decodedId);
  const simulateMutation = useSimulatePolicy(decodedId ?? '');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<SimulatorInputValues>({
    resolver: zodResolver(simulatorInputSchema),
    defaultValues: {
      contextJson: decodedId ? getSampleContext(decodedId, 'allow') : '{}',
    },
  });

  useEffect(() => {
    if (decodedId) {
      setValue('contextJson', getSampleContext(decodedId, 'allow'));
    }
  }, [decodedId, setValue]);

  if (!decodedId) {
    return <Navigate to="/admin/policies" replace />;
  }

  if (policyQuery.isLoading) return <LoadingSkeleton />;
  if (policyQuery.isError || !policyQuery.data) {
    return (
      <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
        <div
          role="alert"
          className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle text-biz-danger border border-biz-danger/30"
        >
          {t('detail.notFound')}
        </div>
      </div>
    );
  }
  const record = policyQuery.data;

  const onSubmit = (values: SimulatorInputValues) => {
    simulateMutation.mutate(values.contextJson);
  };

  const errorFor = (): string | undefined => {
    if (!errors.contextJson) return undefined;
    const msg = errors.contextJson.message;
    if (msg === 'invalidJson') return t('simulator.invalidJson');
    return t('simulator.contextRequired');
  };

  return (
    <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto space-y-biz-section">
      <nav className="text-biz-sm" aria-label={t('detail.breadcrumbLabel')}>
        <ol className="flex items-center gap-biz-content text-biz-muted-foreground">
          <li>
            <Link
              to="/admin/policies"
              className="inline-flex items-center gap-1 hover:text-biz-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring rounded"
            >
              <ArrowLeft size={16} weight="regular" aria-hidden="true" />
              {t('detail.backToList')}
            </Link>
          </li>
          <li aria-hidden="true">
            <CaretRight size={14} weight="regular" />
          </li>
          <li>
            <Link
              to={`/admin/policies/${encodeURIComponent(record.policyId)}`}
              className="hover:text-biz-foreground"
            >
              {record.policyId}
            </Link>
          </li>
          <li aria-hidden="true">
            <CaretRight size={14} weight="regular" />
          </li>
          <li className="text-biz-foreground font-medium">
            {t('simulator.crumb')}
          </li>
        </ol>
      </nav>

      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-biz-content-lg">
        <div className="min-w-0">
          <h1 className="text-biz-3xl biz-heading font-semibold mb-biz-content">
            {t('simulator.title')}
          </h1>
          <p className="text-biz-base text-biz-muted-foreground">
            {t('simulator.subtitle', { policyId: record.policyId, name: record.name })}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-biz-section">
        <Card>
          <CardHeader>
            <div>
              <h3 className="biz-heading font-semibold">{t('simulator.contextTitle')}</h3>
              <p className="text-biz-sm text-biz-muted-foreground">
                {t('simulator.contextHint')}
              </p>
            </div>
          </CardHeader>
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-biz-content-lg">
              <div className="flex flex-wrap gap-biz-content">
                <Button
                  type="button"
                  intent="ghost"
                  size="sm"
                  onClick={() =>
                    setValue('contextJson', getSampleContext(decodedId, 'allow'))
                  }
                >
                  {t('simulator.sampleAllow')}
                </Button>
                <Button
                  type="button"
                  intent="ghost"
                  size="sm"
                  onClick={() =>
                    setValue('contextJson', getSampleContext(decodedId, 'deny'))
                  }
                >
                  {t('simulator.sampleDeny')}
                </Button>
              </div>
              <div>
                <Label htmlFor="sim-context">{t('simulator.contextLabel')}</Label>
                <textarea
                  id="sim-context"
                  rows={18}
                  spellCheck={false}
                  className="w-full font-mono text-biz-sm p-biz-content-lg bg-biz-background border border-biz-input rounded-biz-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring leading-relaxed"
                  aria-invalid={Boolean(errors.contextJson)}
                  {...register('contextJson')}
                />
                <FieldError id="sim-context-error">{errorFor()}</FieldError>
              </div>
              <div className="flex items-center justify-end">
                <Button
                  type="submit"
                  intent="primary"
                  size="lg"
                  leftIcon={<Flask size={20} weight="regular" />}
                  loading={simulateMutation.isPending}
                >
                  {t('simulator.runCta')}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>

        <ResultPanel
          result={simulateMutation.data ?? null}
          isPending={simulateMutation.isPending}
          error={
            simulateMutation.error instanceof Error
              ? simulateMutation.error.message
              : null
          }
        />
      </div>
    </div>
  );
}

function ResultPanel({
  result,
  isPending,
  error,
}: {
  result: SimulatorResult | null;
  isPending: boolean;
  error: string | null;
}): JSX.Element {
  const { t } = useTranslation('adminPolicies');

  if (isPending) {
    return (
      <Card className="px-biz-card-lg py-biz-section" aria-busy="true">
        <div className="h-6 w-1/3 bg-biz-muted rounded animate-pulse mb-biz-content" />
        <div className="h-40 bg-biz-muted rounded animate-pulse" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="px-biz-card-lg py-biz-content-lg">
        <div
          role="alert"
          className="flex items-start gap-biz-content-lg text-biz-danger"
        >
          <Warning weight="regular" size={20} aria-hidden="true" className="shrink-0" />
          <p className="text-biz-sm font-mono">{error}</p>
        </div>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card className="px-biz-card-lg py-biz-section text-biz-muted-foreground">
        <p className="text-biz-sm">{t('simulator.idle')}</p>
      </Card>
    );
  }

  const outcomeIcon =
    result.outcome === 'allow' ? (
      <CheckCircle
        size={28}
        weight="regular"
        aria-hidden="true"
        className="text-biz-success"
      />
    ) : result.outcome === 'deny' ? (
      <XCircle
        size={28}
        weight="regular"
        aria-hidden="true"
        className="text-biz-danger"
      />
    ) : (
      <Warning
        size={28}
        weight="regular"
        aria-hidden="true"
        className="text-biz-warning"
      />
    );

  const outcomeClass =
    result.outcome === 'allow'
      ? 'bg-biz-success-subtle border-biz-success/30 text-biz-success'
      : result.outcome === 'deny'
        ? 'bg-biz-danger-subtle border-biz-danger/30 text-biz-danger'
        : 'bg-biz-warning-subtle border-biz-warning/30 text-biz-warning';

  return (
    <Card>
      <CardHeader>
        <h3 className="biz-heading font-semibold">{t('simulator.resultTitle')}</h3>
        <span className="biz-numeric text-biz-xs text-biz-muted-foreground">
          {t('simulator.resultDuration', { ms: result.durationMs })}
        </span>
      </CardHeader>
      <CardBody className="space-y-biz-content-lg">
        <div
          className={cn(
            'flex items-start gap-biz-content-lg px-biz-content-lg py-biz-content-lg rounded-biz-card border',
            outcomeClass,
          )}
        >
          <span className="shrink-0">{outcomeIcon}</span>
          <div className="min-w-0">
            <div className="text-biz-xl font-bold uppercase tracking-wider">
              {t(`simulator.outcomes.${result.outcome}` as const)}
            </div>
            {result.outcome === 'deny' && result.errorCode ? (
              <div className="mt-biz-content text-biz-sm font-mono">
                {result.statusCode} · {result.errorCode}
              </div>
            ) : null}
            {result.outcome === 'deny' && result.message ? (
              <p className="mt-1 text-biz-sm">{result.message}</p>
            ) : null}
          </div>
        </div>

        <div>
          <h4 className="text-biz-sm font-semibold mb-biz-content">
            {t('simulator.traceTitle')}
          </h4>
          <ol className="space-y-biz-content">
            {result.trace.map((step) => (
              <li
                key={step.conditionId}
                className="flex items-start gap-biz-content-lg px-biz-content-lg py-biz-content rounded-biz-card border border-biz-border bg-biz-surface-elevated"
              >
                <span className="shrink-0">
                  {step.outcome === 'pass' ? (
                    <CheckCircle
                      size={18}
                      weight="regular"
                      aria-hidden="true"
                      className="text-biz-success"
                    />
                  ) : step.outcome === 'fail' ? (
                    <XCircle
                      size={18}
                      weight="regular"
                      aria-hidden="true"
                      className="text-biz-danger"
                    />
                  ) : (
                    <Warning
                      size={18}
                      weight="regular"
                      aria-hidden="true"
                      className="text-biz-warning"
                    />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-biz-content flex-wrap">
                    <span className="font-mono text-biz-xs text-biz-muted-foreground">
                      {step.conditionId}
                    </span>
                    <StatusBadge
                      variant={
                        step.outcome === 'pass'
                          ? 'success'
                          : step.outcome === 'fail'
                            ? 'danger'
                            : 'warning'
                      }
                      label={t(`simulator.trace.${step.outcome}` as const)}
                    />
                    <span className="biz-numeric text-biz-xs text-biz-muted-foreground">
                      {t('simulator.traceDuration', { ms: step.durationMs })}
                    </span>
                  </div>
                  <p className="mt-biz-content text-biz-sm">{step.description}</p>
                  {step.explanation ? (
                    <p className="mt-1 text-biz-xs font-mono text-biz-muted-foreground break-words">
                      {step.explanation}
                    </p>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </CardBody>
    </Card>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div
      className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="h-8 w-1/3 bg-biz-muted rounded animate-pulse mb-biz-content" />
      <div className="h-40 bg-biz-muted rounded-biz-card animate-pulse" />
    </div>
  );
}
